/**
 * Payments Hook
 * Provides payment management operations with React Query
 *
 * @description
 * This hook provides:
 * - Fetch payments with pagination and filters
 * - Fetch single payment by ID with timeline
 * - Fetch payment summary statistics
 * - Approve transaction mutation
 * - Automatic cache invalidation
 *
 * @author SAUBHAGYA Development Team
 * @since Story 4-5
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  manufacturingApi,
  PaymentDTO,
  PaymentDetailDTO,
  PaymentSummaryDTO,
  ApproveTransactionRequest,
  TransactionDTO,
  PageResponse,
} from '@/services/manufacturing/manufacturingApi';

/**
 * Filters for payment list
 */
export interface PaymentFilters {
  status?: string;
  gaushalaSearch?: string;
  gaushalaIds?: number[];
  from?: string;
  to?: string;
  page?: number;
  size?: number;
}

/**
 * Hook to fetch payments with optional filters
 * @param filters - Optional filters (status, gaushalaSearch, from, to, page, size)
 * @param enabled - Whether to enable the query (default: true)
 * @returns Query result with paginated payments
 */
export function usePayments(
  filters?: PaymentFilters,
  enabled: boolean = true
) {
  return useQuery<PageResponse<PaymentDTO>>({
    queryKey: ['payments', filters],
    queryFn: () => manufacturingApi.getPayments(filters),
    enabled,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });
}

/**
 * Hook to fetch a single payment by ID
 * @param paymentId - Payment ID (null to disable query)
 * @param enabled - Whether to enable the query (default: true when paymentId is provided)
 * @returns Query result with payment details including timeline
 */
export function usePaymentDetail(
  paymentId: number | null,
  enabled: boolean = true
) {
  return useQuery<PaymentDetailDTO>({
    queryKey: ['payment', paymentId],
    queryFn: () => manufacturingApi.getPayment(paymentId!),
    enabled: enabled && paymentId !== null && paymentId > 0,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });
}

/**
 * Hook to fetch payment summary statistics
 * @param enabled - Whether to enable the query (default: true)
 * @returns Query result with payment summary data
 */
export function usePaymentSummary(enabled: boolean = true) {
  return useQuery<PaymentSummaryDTO>({
    queryKey: ['payment-summary'],
    queryFn: () => manufacturingApi.getPaymentSummary(),
    enabled,
    staleTime: 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });
}

/**
 * Hook to approve a transaction and initiate payment
 * @returns Mutation object with approveTransaction function
 */
export function useApproveTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      transactionId,
      data,
    }: {
      transactionId: number;
      data: ApproveTransactionRequest;
    }) => manufacturingApi.approveTransaction(transactionId, data),
    onSuccess: (_, variables) => {
      // Invalidate and refetch transactions list
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['transaction-summary'] });
      // Invalidate specific transaction
      queryClient.invalidateQueries({
        queryKey: ['transaction', variables.transactionId],
      });
      // Invalidate payments list and summary
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['payment-summary'] });
    },
  });
}

/**
 * Hook to manually refetch payments
 * @returns Function to refetch payments
 */
export function useRefetchPayments() {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: ['payments'] });
    queryClient.invalidateQueries({ queryKey: ['payment-summary'] });
  };
}
